import React, { useState, useRef } from 'react';
import { Stage, Layer, Image, Rect, Circle, Text } from 'react-konva';

interface KnownPlacement {
  id?: string;
  _id?: { $oid: string };
  charm_id: string;
  charm_name: string;
  posx: number;
  posy: number;
  x: number;
  y: number;
  z: number;
  islegacy: boolean;
  isactive: boolean;
  inspectlink?: string;
  charmlink_image?: string; // Added for loading the actual picture
}

interface CanvasEditorProps {
  imagePosition: { x: number; y: number };
  onPositionChange: (position: { x: number; y: number }) => void;
  overlayImageUrl?: string;
  backgroundImageUrl?: string;
  existingPlacements?: KnownPlacement[];
  width?: number;
  height?: number;
  charmScale?: number;
  scale?: number; // Add scale prop
  stagePosition?: { x: number; y: number };
  onScaleChange?: (scale: number) => void;
  onStagePositionChange?: (pos: { x: number; y: number }) => void;
  showDots?: boolean;
}

const CanvasEditor: React.FC<CanvasEditorProps> = ({
  imagePosition,
  onPositionChange,
  overlayImageUrl,
  backgroundImageUrl,
  existingPlacements = [],
  width = 1920,
  height = 1080,
  charmScale = 1.0,
  scale = 0.8,
  stagePosition = { x: 0, y: 0 },
  onScaleChange,
  onStagePositionChange,
  showDots = true,
}) => {
  const [backgroundImage, setBackgroundImage] = useState<HTMLImageElement | null>(null);
  const [overlayImage, setOverlayImage] = useState<HTMLImageElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  
  const stageRef = useRef<any>(null);
  
  // Canvas dimensions
  // const scale = 0.8; // 80% scale for better fit
  
  // Charm dimensions (container)
  const charmWidth = 100.37520114398815;
  const charmHeight = 75.28140085799095;
  
  // Bounding box constraints - hide top and bottom 20%
  const topOffset = height * 0 // 0.2; // 20% of 1080 = 216px
  const bottomOffset = height * 0 // 0.8; // 80% of 1080 = 864px
  const boundingBox = {
    x: 70,
    y: topOffset, // Start 20% down from the top
    width: width - 90, // 1920 - 90 = 1830
    height: bottomOffset - topOffset, // Show only middle 60%
  };

  // Calculate scaled dimensions while maintaining aspect ratio
  const calculateScaledDimensions = (imgWidth: number, imgHeight: number) => {
    const aspectRatio = imgWidth / imgHeight;
    const containerAspectRatio = charmWidth / charmHeight;
    
    let scaledWidth, scaledHeight;
    
    if (aspectRatio > containerAspectRatio) {
      // Image is wider than container, fit to width
      scaledWidth = charmWidth;
      scaledHeight = charmWidth / aspectRatio;
    } else {
      // Image is taller than container, fit to height
      scaledHeight = charmHeight;
      scaledWidth = charmHeight * aspectRatio;
    }
    
    return { width: scaledWidth, height: scaledHeight };
  };

  // Load background image
  React.useEffect(() => {
    const bgImg = new window.Image();
    bgImg.crossOrigin = 'anonymous';
    bgImg.onload = () => {
      setBackgroundImage(bgImg);
    };
    // Use backgroundImageUrl if present, otherwise fallback
    bgImg.src = backgroundImageUrl && backgroundImageUrl.trim() !== ''
      ? backgroundImageUrl
      : 'https://cdn.cs2inspects.com/customizer/7_44_0.webp';
  }, [backgroundImageUrl]);

  // Load overlay image
  React.useEffect(() => {
    const overlayImg = new window.Image();
    overlayImg.crossOrigin = 'anonymous';
    overlayImg.onload = () => {
      setOverlayImage(overlayImg);
      const dimensions = calculateScaledDimensions(overlayImg.width, overlayImg.height);
      setImageDimensions(dimensions);
    };
    // Use overlayImageUrl if present, otherwise fallback
    overlayImg.src = overlayImageUrl && overlayImageUrl.trim() !== ''
      ? overlayImageUrl
      : 'https://img.cs2inspects.com/1355_2_1_front.webp';
  }, [overlayImageUrl]);

  const handleDragStart = () => {
    setIsDragging(true);
  };

  // Center-based drag logic
  const handleDragEnd = (e: any) => {
    setIsDragging(false);
    const node = e.target;
    const newX = node.x();
    const newY = node.y();
    const w = imageDimensions.width * charmScale;
    const h = imageDimensions.height * charmScale;
    // Position charm so top center is at the saved position
    const topCenterX = newX + w / 2;
    const topCenterY = newY;
    node.position({ x: topCenterX - w / 2, y: topCenterY });
    onPositionChange({ x: topCenterX, y: topCenterY });
  };

  // Mouse wheel zoom handler
  const handleWheel = (e: any) => {
    e.evt.preventDefault();
    const scaleBy = 1.05;
    let newScale = scale;
    if (e.evt.deltaY < 0) {
      newScale = Math.min(scale * scaleBy, 5.0);
    } else {
      newScale = Math.max(scale / scaleBy, 0.2);
    }
    // Get mouse position relative to stage
    const stage = stageRef.current;
    if (!stage) return;
    const pointer = stage.getPointerPosition();
    if (!pointer) return;
    const mousePointTo = {
      x: (pointer.x - stagePosition.x) / scale,
      y: (pointer.y - stagePosition.y) / scale,
    };
    // Calculate new position so zoom is at mouse
    const newPos = {
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    };
    if (onScaleChange) onScaleChange(newScale);
    if (onStagePositionChange) onStagePositionChange(newPos);
  };

  // Click handler to position charm at clicked point
  const handleStageClick = (e: any) => {
    const stage = stageRef.current;
    if (!stage) return;
    
    const pointer = stage.getPointerPosition();
    if (!pointer) return;
    
    // Convert pointer position to stage coordinates
    const mousePointTo = {
      x: (pointer.x - stagePosition.x) / scale,
      y: (pointer.y - stagePosition.y) / scale,
    };
    
    // Position the charm so that the top center is at the clicked point
    const newPosition = {
      x: mousePointTo.x,
      y: mousePointTo.y, // Top center is at clicked point
    };
    
    onPositionChange(newPosition);
  };

  return (
    <div className="canvas-container">
      <Stage
        ref={stageRef}
        width={width * scale}
        height={height * scale}
        scale={{ x: scale, y: scale }}
        x={stagePosition.x}
        y={stagePosition.y}
        onWheel={handleWheel}
        onClick={handleStageClick}
      >
        <Layer>
          {/* Top mask to hide 20% */}
          <Rect
            x={0}
            y={0}
            width={width}
            height={topOffset}
            fill="#f0f0f0"
            opacity={0.9}
          />
          
          {/* Bottom mask to hide 20% */}
          <Rect
            x={0}
            y={bottomOffset}
            width={width}
            height={height - bottomOffset}
            fill="#f0f0f0"
            opacity={0.9}
          />
          
          {/* Background Image */}
          {backgroundImage && (
            <Image
              image={backgroundImage}
              x={0}
              y={0}
              width={width}
              height={height}
            />
          )}
          
          {/* Bounding Box (visual guide) */}
          <Rect
            x={boundingBox.x}
            y={boundingBox.y}
            width={boundingBox.width}
            height={boundingBox.height}
            stroke="#00ff00"
            strokeWidth={2}
            dash={[5, 5]}
            fill="rgba(0, 255, 0, 0.1)"
          />
          
          {/* Charm container outline (scales with charm) */}
          <Rect
            x={imagePosition.x - (charmWidth * charmScale) / 2}
            y={imagePosition.y}
            width={charmWidth * charmScale}
            height={charmHeight * charmScale}
            stroke="#ff0000"
            strokeWidth={1}
            dash={[3, 3]}
            fill="rgba(255, 0, 0, 0.05)"
          />
          
          {/* Overlay Image (top center positioned) */}
          {overlayImage && (
            <Image
              key={overlayImageUrl || 'default-overlay'}
              image={overlayImage}
              x={imagePosition.x - (imageDimensions.width * charmScale) / 2}
              y={imagePosition.y}
              width={imageDimensions.width * charmScale}
              height={imageDimensions.height * charmScale}
              draggable
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              opacity={isDragging ? 0.8 : 1}
            />
          )}
          
          {/* X marks for existing placements - positioned at top center */}
          {showDots && existingPlacements.filter(p => p.isactive).map((placement, index) => (
            <Text
              key={placement._id?.$oid || placement.id || index}
              x={placement.posx - 4}
              y={placement.posy - 6}
              text="×"
              fontSize={12}
              fill="#00ff00"
              opacity={0.8}
              fontStyle="normal"
            />
          ))}
          
          {/* Red X at top center of charm image - positioned on top */}
          {overlayImage && (
            <Text
              x={imagePosition.x - 4}
              y={imagePosition.y - 6}
              text="×"
              fontSize={12}
              fill="#ff0000"
              opacity={1}
              fontStyle="normal"
            />
          )}
        </Layer>
      </Stage>
    </div>
  );
};

export default CanvasEditor; 